import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock, CalendarPlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState, RelativeTime } from '../../../components/design-system/admin';
import { DEMO_STATUSES, DemoRequest, DemoStatus, demoId } from '../../../common/models/admin-signups.model';
import { formatSlot, localTimeZone, meetInviteUrl } from '../../signup/demo-slots';
import { enumKey } from './signup-parts';

/** The internal note, saved when it loses focus (or on Enter) — only if it changed. */
const NoteField = ({ request, onSave }: { request: DemoRequest; onSave: (note: string) => void }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(request.note || '');
  const save = () => {
    if (value.trim() !== (request.note || '').trim()) onSave(value.trim());
  };
  return (
    <Input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          save();
        }
      }}
      placeholder={t('ADMIN_SIGNUPS.DEMO.NOTE_PLACEHOLDER')}
      aria-label={t('ADMIN_SIGNUPS.DEMO.NOTE')}
      className="h-8 w-full min-w-[120px]"
      data-testid={`demo-note-${demoId(request)}`}
    />
  );
};

/** Their message, clamped to three lines; "Show more" opens it in full when it runs longer. */
const DemoMessage = ({ id, message }: { id: string; message: string }) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !expanded) setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [message, expanded]);
  return (
    <div className="mt-1">
      <p ref={ref} className={`whitespace-pre-line break-words text-xs text-muted-foreground ${expanded ? '' : 'line-clamp-3'}`} data-testid={`demo-message-${id}`}>
        {message}
      </p>
      {(overflows || expanded) && (
        <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded} data-testid={`demo-message-toggle-${id}`}>
          {t(expanded ? 'ADMIN_SIGNUPS.DEMO.MESSAGE_LESS' : 'ADMIN_SIGNUPS.DEMO.MESSAGE_MORE')}
        </Button>
      )}
    </div>
  );
};

/**
 * Everyone who asked for a demo: who, what they sell and why, when they
 * would like it, and where it stands. Status and note are edited in place.
 */

/**
 * When they asked for the call: in the reviewer's own time, their local time
 * beside it when the zones differ, and "Send invite", which opens Google
 * Calendar with the slot, their address as guest and the context filled in.
 */
const DemoWhen = ({ request: r }: { request: DemoRequest }) => {
  const { t, i18n } = useTranslation();
  const at = r.preferredAt ? new Date(r.preferredAt) : null;
  if (!at || !Number.isFinite(at.getTime())) return <span>{r.preferredTime || '—'}</span>;
  const mine = localTimeZone();
  const theirs = r.timeZone || undefined;
  const invite = meetInviteUrl({
    at,
    email: r.email,
    title: t('ADMIN_SIGNUPS.DEMO.INVITE_TITLE', { who: r.company || r.name }),
    details: [
      t('ADMIN_SIGNUPS.DEMO.INVITE_INTRO', { name: r.name }),
      r.website ? `${t('ADMIN_SIGNUPS.DEMO.INVITE_WEBSITE')}: ${r.website}` : null,
      r.phone ? `${t('ADMIN_SIGNUPS.DEMO.INVITE_PHONE')}: ${r.phone}` : null,
      r.message ? `\n${r.message}` : null,
    ]
      .filter(Boolean)
      .join('\n'),
  });
  return (
    <div className="grid gap-1">
      <span className="whitespace-nowrap tabular-nums">{formatSlot(at, i18n.language || 'en', undefined, true)}</span>
      {theirs && theirs !== mine && (
        <span className="text-xs text-muted-foreground tabular-nums">
          {t('ADMIN_SIGNUPS.DEMO.THEIR_TIME', { time: formatSlot(at, i18n.language || 'en', theirs, true), zone: theirs })}
        </span>
      )}
      <Button variant="outline" size="sm" className="mt-1 h-7 w-fit" asChild>
        <a href={invite} target="_blank" rel="noopener noreferrer" data-testid={`demo-invite-${r.id || r._id || ''}`}>
          <CalendarPlus data-icon="inline-start" />
          {t('ADMIN_SIGNUPS.DEMO.SEND_INVITE')}
        </a>
      </Button>
    </div>
  );
};

export function DemoRequestsTable({
  rows,
  loading,
  onUpdate,
  onOpenAccount,
}: {
  rows: DemoRequest[];
  loading?: boolean;
  onUpdate: (request: DemoRequest, patch: { status?: DemoStatus; note?: string }) => void;
  onOpenAccount: (vendorId: string) => void;
}) {
  const { t } = useTranslation();

  if (loading && !rows.length) {
    return (
      <div className="grid gap-2 p-4">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }
  if (!rows.length) {
    return <EmptyState icon={<CalendarClock />} title={t('ADMIN_SIGNUPS.EMPTY.DEMOS_TITLE')} description={t('ADMIN_SIGNUPS.EMPTY.DEMOS_BODY')} />;
  }

  return (
    <Table data-testid="demo-table">
      <TableHeader>
        <TableRow>
          <TableHead className="ps-4">{t('ADMIN_SIGNUPS.DEMO.REQUESTER')}</TableHead>
          <TableHead>{t('ADMIN_SIGNUPS.DEMO.COMPANY')}</TableHead>
          <TableHead>{t('ADMIN_SIGNUPS.DEMO.WHY')}</TableHead>
          <TableHead>{t('ADMIN_SIGNUPS.DEMO.PREFERRED')}</TableHead>
          <TableHead>{t('ADMIN_SIGNUPS.DEMO.RECEIVED')}</TableHead>
          <TableHead>{t('ADMIN_SIGNUPS.DEMO.STATUS_LABEL')}</TableHead>
          <TableHead className="pe-4">{t('ADMIN_SIGNUPS.DEMO.NOTE')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => {
          const id = demoId(r);
          return (
            <TableRow key={id || r.createdAt} data-testid={`demo-row-${id}`} className="align-top">
              <TableCell className="ps-4">
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-muted-foreground" dir="ltr">{r.email}</div>
                {r.phone && (
                  <div className="text-xs text-muted-foreground tabular-nums" dir="ltr">
                    {r.phone}
                  </div>
                )}
              </TableCell>
              <TableCell>
                <div>{r.company || '—'}</div>
                {r.website && (
                  <div className="text-xs text-muted-foreground" dir="ltr">
                    {r.website}
                  </div>
                )}
                {r.demoSlug && (
                  <a
                    href={`https://${r.demoSlug}.ardealy.link`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-xs text-muted-foreground underline-offset-2 hover:underline"
                    data-testid={`demo-from-${id}`}
                  >
                    {t('ADMIN_SIGNUPS.DEMO.FROM_DEMO', { demo: `${r.demoSlug}.ardealy.link` })}
                  </a>
                )}
                {r.vendor && (
                  <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => onOpenAccount(String(r.vendor))} data-testid={`demo-account-${id}`}>
                    {t('ADMIN_SIGNUPS.DEMO.OPEN_ACCOUNT')}
                  </Button>
                )}
              </TableCell>
              <TableCell className="min-w-[180px] max-w-[230px] whitespace-normal">
                <div>{t(`ADMIN_SIGNUPS.DEMO.REASONS.${enumKey(r.reason)}`)}</div>
                <div className="text-xs text-muted-foreground">{t(`ADMIN_SIGNUPS.DEMO.SELLS.${enumKey(r.sells)}`)}</div>
                {r.message && <DemoMessage id={id} message={r.message} />}
              </TableCell>
              <TableCell className="min-w-[170px] whitespace-normal">
                <DemoWhen request={r} />
              </TableCell>
              <TableCell>
                <RelativeTime value={r.createdAt} className="text-muted-foreground" />
              </TableCell>
              <TableCell>
                <Select value={r.status} onValueChange={(v) => onUpdate(r, { status: v as DemoStatus })}>
                  <SelectTrigger size="sm" className="w-[130px]" aria-label={t('ADMIN_SIGNUPS.DEMO.STATUS_FOR', { name: r.name })}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEMO_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {t(`ADMIN_SIGNUPS.DEMO.STATUS.${enumKey(s)}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="pe-4">
                <NoteField request={r} onSave={(note) => onUpdate(r, { note })} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
